import { isNil } from 'lodash-es';

import { themeVars } from '../theme/theme';

/**
 * A namespace containing utility functions for number parsing, validation, and formatting
 */
export namespace Numbers {
  /**
   * Parses a value into a number, handling various input types
   * @param value - The value to parse (can be number, string, or null/undefined)
   * @returns The parsed number or NaN if parsing fails
   */
  export const parse = (value: number | string | null | undefined): number => {
    if (isNil(value)) {
      return NaN;
    }

    if (typeof value === 'number' && !isNaN(value)) {
      return value;
    }

    const parsedValue = typeof value === 'string' ? parseFloat(value) : NaN;

    if (!isNaN(parsedValue)) {
      return parsedValue;
    }

    return NaN;
  };

  /**
   * Checks if a value is a positive number
   * @param value - The value to check
   * @returns True if the value is a positive number, false otherwise
   */
  export const isPositive = (value: number | string | null | undefined) => {
    return isNumber(value) && value > 0;
  };

  /**
   * Checks if a value is a negative number
   * @param value - The value to check
   * @returns True if the value is a negative number, false otherwise
   */
  export const isNegative = (value: number | string | null | undefined) => {
    return isNumber(value) && value < 0;
  };

  export const getDynamic = (v: number | string | null | undefined): DynamicType | null => {
    const value = parse(v);
    if (!isNumber(value)) return null;
    if (isPositive(value)) return 'positive';
    if (isNegative(value)) return 'negative';
    return 'neutral';
  };

  /**
   * Checks if a value is a valid number
   * @param value - The value to check
   * @returns True if the value is a valid number, false otherwise
   */
  export const isNumber = (value: number | string | null | undefined): value is number => {
    return typeof value === 'number' && !isNaN(value);
  };

  /**
   * Type definition for number formatting styles
   */
  export type NumberType = 'number' | 'percent' | 'currency';

  /**
   * Configuration options for number formatting
   */
  export type FormatterProps = Intl.NumberFormatOptions & {
    /** The locale to use for formatting */
    locale?: Intl.LocalesArgument;
    /** Whether to force decimal places */
    decimal?: boolean;
    /** The value to return when formatting fails */
    invalidValue?: string;
  };

  /**
   * Formats a number according to the specified options
   * @param v - The value to format
   * @param options - Formatting options including locale, style, and currency
   * @returns The formatted string or the invalidValue if formatting fails
   */
  export const format = (v: number | string | null | undefined, _options: FormatterProps = {}) => {
    const {
      locale = 'en-US',
      invalidValue = 'n/a',
      style,
      currency,
      decimal,
      minimumFractionDigits,
      maximumFractionDigits,
      ...options
    } = _options;
    const value = parse(v);
    if (isNaN(value)) return invalidValue;

    const formatter = new Intl.NumberFormat(locale, {
      style,
      ...(style === 'currency' ? { currency: currency || 'USD', currencyDisplay: 'symbol' } : {}),
      minimumFractionDigits: minimumFractionDigits ?? (decimal ? 2 : 0),
      maximumFractionDigits: maximumFractionDigits ?? (decimal ? 2 : 0),
      ...options,
    });

    try {
      return formatter.format(value);
    } catch {
      return invalidValue;
    }
  };

  /**
   * Formats a number as currency
   * @param v - The value to format
   * @param options - Formatting options including locale and currency
   * @returns The formatted currency string
   */
  export const formatCurrency = (
    v: number | string | null | undefined,
    options?: FormatterProps,
  ) => {
    return format(v, { style: 'currency', ...options });
  };

  /**
   * Formats a number as a percentage
   * @param v - The value to format
   * @param options - Formatting options including locale
   * @returns The formatted percentage string
   */
  export const formatPercents = (
    v: number | string | null | undefined,
    options?: FormatterProps,
  ) => {
    return format(v, { style: 'percent', ...options });
  };

  export type DynamicType = 'positive' | 'negative' | 'neutral';

  export const dynamicsColors: Record<DynamicType, string> = {
    positive: themeVars.colors.success600,
    negative: themeVars.colors.error550,
    neutral: themeVars.colors.coldGrey500,
  };

  const formattersMap: Record<NumberType, typeof format> = {
    currency: formatCurrency,
    number: format,
    percent: formatPercents,
  };

  export const formatByType = (
    type: NumberType,
    v: number | string | null | undefined,
    options?: FormatterProps,
  ) => {
    return formattersMap[type](v, options);
  };
}
